import type { SyntheticEvent } from 'react';

/**
 * Stop an interaction inside a debug surface from reaching the page's input capture.
 *
 * InputCapture registers mouse and touch listeners on `window` and calls `preventDefault()`
 * on every one. On a touch device that suppresses the synthesized `click`, which is why
 * the debug panels' close buttons did nothing on a phone. React delegates at its root
 * container, which sits below `window` in the bubble path, so stopping propagation on the
 * synthetic event keeps the touch from ever reaching those listeners: the click is
 * synthesized normally, and the tap is not forwarded to the host as game input either.
 */
export function isolateFromInputCapture(event: SyntheticEvent): void {
  event.stopPropagation();
}

/** Spread onto the root element of any debug surface. */
export const DEBUG_SURFACE_EVENT_ISOLATION = {
  onPointerDown: isolateFromInputCapture,
  onPointerUp: isolateFromInputCapture,
  onMouseDown: isolateFromInputCapture,
  onMouseUp: isolateFromInputCapture,
  onTouchStart: isolateFromInputCapture,
  onTouchEnd: isolateFromInputCapture,
  onTouchMove: isolateFromInputCapture,
  onWheel: isolateFromInputCapture,
  onKeyDown: isolateFromInputCapture,
  onKeyUp: isolateFromInputCapture,
} as const;
